import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Loader2, Paperclip, ShieldAlert, Download } from "lucide-react";
import { api } from "@/lib/accessApi";

export default function ProductView() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, product: null, error: null, checkoutUrl: "" });

  useEffect(() => {
    (async () => {
      try {
        const product = await api(`/products/${productId}`);
        setState({ loading: false, product, error: null, checkoutUrl: "" });
      } catch (err) {
        if (err.status === 401) return navigate("/access", { replace: true });
        let checkoutUrl = "";
        try { checkoutUrl = (await api("/access/config")).checkout_url || ""; } catch { /* ignore */ }
        setState({ loading: false, product: null, error: err.status === 403 ? "no-entitlement" : "not-found", checkoutUrl });
      }
    })();
  }, [productId, navigate]);

  if (state.loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (state.error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-4 text-center">
        <ShieldAlert className="h-10 w-10 text-amber-400" />
        <h1 className="mt-4 text-lg font-semibold text-white" data-testid="product-error">
          {state.error === "no-entitlement" ? "You don't have access to this product." : "Product not found."}
        </h1>
        <p className="mt-1 text-sm text-slate-400">Access is verified on the server for every request.</p>
        {state.error === "no-entitlement" && state.checkoutUrl && (
          <a href={state.checkoutUrl} target="_blank" rel="noopener noreferrer" data-testid="checkout-btn"
            className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-indigo-500 to-sky-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
            Beli di Lynk.id
          </a>
        )}
        <Link to="/dashboard" className="mt-3 rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-200 hover:bg-white/5">Back to dashboard</Link>
      </div>
    );
  }

  const p = state.product;
  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          <button onClick={() => navigate("/dashboard")} data-testid="back-btn" className="flex items-center gap-1 text-sm text-slate-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10" data-testid="product-content">
        <h1 className="text-2xl font-bold text-white">{p.name}</h1>
        <p className="mt-1 text-slate-400">{p.description}</p>

        <article className="mt-8 max-w-none leading-relaxed text-slate-200 [&_a]:text-indigo-300 [&_a]:underline [&_code]:rounded [&_code]:bg-white/10 [&_code]:px-1 [&_h1]:mt-6 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-white [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h3]:mt-4 [&_h3]:font-semibold [&_h3]:text-white [&_li]:my-1 [&_li]:text-slate-200 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-3 [&_strong]:text-white [&_ul]:list-disc [&_ul]:pl-5">
          <ReactMarkdown>{p.content_md || "_No content yet._"}</ReactMarkdown>
        </article>

        {p.attachments?.length > 0 && (
          <div className="mt-10">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">Attachments</h2>
            <ul className="space-y-2">
              {p.attachments.map((a, i) => (
                <li key={`${a.url}-${i}`}>
                  <a
                    href={a.url} target="_blank" rel="noopener noreferrer" data-testid={`attachment-${i}`}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 hover:border-indigo-400/40 hover:bg-white/10"
                  >
                    <span className="flex items-center gap-2"><Paperclip className="h-4 w-4 text-indigo-300" /> {a.label || a.url}</span>
                    <Download className="h-4 w-4 text-slate-400" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
